import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ProtectedRoutes from "./utils/ProtectedRoutes";
import SignIn from "./pages/SignIn";
import Register from "./pages/RegisterForm";
import Dashboard from "./pages/Dashboard";
import Regions from "./pages/Regions";
import Places from "./pages/Places";
import Devices from "./pages/Devices";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Stocks from "./pages/Stock";
import NodeDetail from "./pages/NodeDetail";
import Telemetry from "./pages/Telemetry";
import { RefreshIntervalProvider } from "./contexts/RefreshIntervalContext";
import { getCurrentTenant } from "./config/tenantConfig";
import AdminConfigurations from "./pages/AdminConfigurations";
import ProjectList from "./pages/ProjectList";
import ErrorBoundary from "./components/ErrorBoundary";
import ErrorTestButton from "./components/ErrorTestButton";
import VersionUpdatePopup from "./components/VersionUpdatePopup";

function App() {
  // Get the tenant configuration based on the current hostname
  const tenantConfig = getCurrentTenant();
  const { primary } = tenantConfig.theme;

  // Set document title based on tenant
  useEffect(() => {
    document.title = tenantConfig.title || tenantConfig.name;

    // Update favicon if tenant has one
    if (tenantConfig.favicon) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');
      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';
      link.href = tenantConfig.favicon;
      document.getElementsByTagName('head')[0].appendChild(link);
    }
  }, [tenantConfig.title, tenantConfig.name, tenantConfig.favicon]);

  // Create MUI theme based on tenant's primary color
  const muiTheme = createTheme({
    palette: {
      primary: { main: primary },
    },
  });

  return (
    <ThemeProvider theme={muiTheme}>
      <ErrorBoundary>
        <VersionUpdatePopup />
        <Router>
          <RefreshIntervalProvider>
            <Routes>
              {/* Root redirects based on authToken in localStorage */}
              <Route
                path="/"
                element={
                  localStorage.getItem("authToken") ? (
                    <Navigate to="/dashboard" />
                  ) : (
                    <Navigate to="/login" />
                  )
                }
              />

              {/* Pass tenant information to the SignIn component */}
              <Route
                path="/login"
                element={<SignIn tenantConfig={tenantConfig} />}
              />

              {/* Register route - only for default tenant */}
              {tenantConfig.name === "LettaIoT" && (
                <Route
                  path="/register"
                  element={<Register tenantConfig={tenantConfig} />}
                />
              )}

              {/* Protected routes */}
              <Route element={<ProtectedRoutes />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/regions" element={<Regions />} />
                <Route path="/places" element={<Places />} />
                <Route path="/places/:idNode" element={<Telemetry />} />
                <Route path="/vending-definitions" element={<Places />} />
                <Route path="/vending-machines/:idNode" element={<Telemetry />} />
                <Route path="/vending-machines" element={<NodeDetail />} />
                <Route path="/node-detail" element={<NodeDetail />} />
                <Route path="/stock-management" element={<Stocks />} />
                <Route path="/devices" element={<Devices />} />
                <Route
                  path="/admin-configurations"
                  element={<AdminConfigurations />}
                />
                <Route path="/users" element={<Users />} />
              </Route>
              <Route path="/projects" element={<ProjectList />} />
              <Route path="/reports" element={<Reports />} />
            </Routes>
            {/* Error Boundary test butonu - sadece development */}
            {process.env.NODE_ENV === "development" && <ErrorTestButton />}
          </RefreshIntervalProvider>
        </Router>
      </ErrorBoundary>
    </ThemeProvider>
  );
}

export default App;
